import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables');
  console.error('VITE_SUPABASE_URL:', supabaseUrl);
  console.error('VITE_SUPABASE_ANON_KEY:', supabaseAnonKey ? 'exists' : 'missing');
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  db: {
    schema: 'public',
  },
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
  global: {
    headers: {
      'Prefer': 'return=representation',
    },
  },
});

// Database types
export type Database = {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string;
          name: string;
          email: string;
          phone?: string;
          role: 'admin' | 'branch' | 'unit' | 'student';
          avatar?: string;
          branch_id?: string;
          unit_id?: string;
          unit_type?: 'education_consultant' | 'visa_consultant' | 'marketing' | 'accounting' | 'customer_service' | 'sales';
          is_active: boolean;
          permissions: string[];
          responsible_universities: string[];
          created_at: string;
          last_login?: string;
          updated_at: string;
        };
        Insert: {
          id: string;
          name: string;
          email: string;
          phone?: string;
          role: 'admin' | 'branch' | 'unit' | 'student';
          avatar?: string;
          branch_id?: string;
          unit_id?: string;
          unit_type?: 'education_consultant' | 'visa_consultant' | 'marketing' | 'accounting' | 'customer_service' | 'sales';
          is_active?: boolean;
          permissions?: string[];
          responsible_universities?: string[];
          last_login?: string;
        };
        Update: {
          name?: string;
          email?: string;
          phone?: string;
          avatar?: string;
          is_active?: boolean;
          permissions?: string[];
          responsible_universities?: string[];
          last_login?: string;
          updated_at?: string;
        };
      };
      students: {
        Row: {
          id: string;
          first_name: string;
          last_name: string;
          email: string;
          phone?: string;
          birth_date?: string;
          nationality: string;
          passport_number?: string;
          branch_id?: string;
          unit_id?: string;
          unit_type: string;
          assigned_consultants: string[];
          current_unit: string;
          status: string;
          source: string;
          interested_countries: string[];
          interested_programs: string[];
          interested_universities: string[];
          academic_year?: string;
          has_other_citizenship: boolean;
          other_citizenship_country?: string;
          has_italy_residence_permit: boolean;
          meeting_date?: string;
          meeting_consultant?: string;
          meeting_type?: string;
          interested_service?: string;
          service_year?: string;
          is_serious_candidate?: boolean;
          visa_info_provided?: boolean;
          meeting_summary?: string;
          discussed_price?: string;
          has_discount?: boolean;
          discount_info?: string;
          contract_sent?: boolean;
          form_completed?: boolean;
          email_sent?: boolean;
          follow_up_status?: string;
          follow_up_date?: string;
          next_meeting_date?: string;
          next_meeting_plan?: string;
          reminders?: string;
          reference_source?: string;
          is_registered?: boolean;
          lead_score: number;
          budget_score: number;
          documents_score: number;
          language_score: number;
          experience_score: number;
          urgency_score: number;
          budget?: string;
          english_level?: string;
          education_level?: string;
          work_experience?: string;
          priority: string;
          tags: string[];
          voice_notes: any[];
          created_at: string;
          last_contact?: string;
          next_follow_up?: string;
          updated_at: string;
        };
        Insert: {
          first_name: string;
          last_name: string;
          email: string;
          phone?: string;
          birth_date?: string;
          nationality?: string;
          passport_number?: string;
          branch_id?: string;
          unit_id?: string;
          unit_type: string;
          assigned_consultants?: string[];
          current_unit: string;
          status?: string;
          source?: string;
          interested_countries?: string[];
          interested_programs?: string[];
          interested_universities?: string[];
          academic_year?: string;
          has_other_citizenship?: boolean;
          other_citizenship_country?: string;
          has_italy_residence_permit?: boolean;
          meeting_date?: string;
          meeting_consultant?: string;
          meeting_type?: string;
          interested_service?: string;
          service_year?: string;
          is_serious_candidate?: boolean;
          visa_info_provided?: boolean;
          meeting_summary?: string;
          discussed_price?: string;
          has_discount?: boolean;
          discount_info?: string;
          contract_sent?: boolean;
          form_completed?: boolean;
          email_sent?: boolean;
          follow_up_status?: string;
          follow_up_date?: string;
          next_meeting_date?: string;
          next_meeting_plan?: string;
          reminders?: string;
          reference_source?: string;
          is_registered?: boolean;
          lead_score?: number;
          budget_score?: number;
          documents_score?: number;
          language_score?: number;
          experience_score?: number;
          urgency_score?: number;
          budget?: string;
          english_level?: string;
          education_level?: string;
          work_experience?: string;
          priority?: string;
          tags?: string[];
          voice_notes?: any[];
          last_contact?: string;
          next_follow_up?: string;
        };
        Update: {
          first_name?: string;
          last_name?: string;
          email?: string;
          phone?: string;
          birth_date?: string;
          nationality?: string;
          passport_number?: string;
          branch_id?: string;
          unit_id?: string;
          unit_type?: string;
          assigned_consultants?: string[];
          current_unit?: string;
          status?: string;
          source?: string;
          interested_countries?: string[];
          interested_programs?: string[];
          interested_universities?: string[];
          academic_year?: string;
          has_other_citizenship?: boolean;
          other_citizenship_country?: string;
          has_italy_residence_permit?: boolean;
          meeting_date?: string;
          meeting_consultant?: string;
          meeting_type?: string;
          interested_service?: string;
          service_year?: string;
          is_serious_candidate?: boolean;
          visa_info_provided?: boolean;
          meeting_summary?: string;
          discussed_price?: string;
          has_discount?: boolean;
          discount_info?: string;
          contract_sent?: boolean;
          form_completed?: boolean;
          email_sent?: boolean;
          follow_up_status?: string;
          follow_up_date?: string;
          next_meeting_date?: string;
          next_meeting_plan?: string;
          reminders?: string;
          reference_source?: string;
          is_registered?: boolean;
          lead_score?: number;
          budget_score?: number;
          documents_score?: number;
          language_score?: number;
          experience_score?: number;
          urgency_score?: number;
          budget?: string;
          english_level?: string;
          education_level?: string;
          work_experience?: string;
          priority?: string;
          tags?: string[];
          voice_notes?: any[];
          last_contact?: string;
          next_follow_up?: string;
          updated_at?: string;
        };
      };
      applications: {
        Row: {
          id: string;
          student_id: string;
          student_name: string;
          country: string;
          university: string;
          program: string;
          degree: string;
          start_date: string;
          application_fee: number;
          tuition_fee: number;
          status: string;
          submitted_at?: string;
          response_date?: string;
          notes?: string;
          documents: string[];
          submitted_by?: string;
          requirements: string[];
          deadline?: string;
          acceptance_letter?: string;
          rejection_reason?: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          student_id: string;
          student_name: string;
          country: string;
          university: string;
          program: string;
          degree: string;
          start_date: string;
          application_fee?: number;
          tuition_fee?: number;
          status?: string;
          submitted_at?: string;
          response_date?: string;
          notes?: string;
          documents?: string[];
          submitted_by?: string;
          requirements?: string[];
          deadline?: string;
          acceptance_letter?: string;
          rejection_reason?: string;
        };
        Update: {
          student_name?: string;
          country?: string;
          university?: string;
          program?: string;
          degree?: string;
          start_date?: string;
          application_fee?: number;
          tuition_fee?: number;
          status?: string;
          submitted_at?: string;
          response_date?: string;
          notes?: string;
          documents?: string[];
          submitted_by?: string;
          requirements?: string[];
          deadline?: string;
          acceptance_letter?: string;
          rejection_reason?: string;
          updated_at?: string;
        };
      };
      proposals: {
        Row: {
          id: string;
          student_id: string;
          student_name: string;
          university: string;
          program: string;
          degree: string;
          total_amount: number;
          application_fee: number;
          service_fee: number;
          currency: string;
          valid_until: string;
          include_visa: boolean;
          include_accommodation: boolean;
          include_insurance: boolean;
          payment_plan: string;
          status: string;
          notes?: string;
          created_by?: string;
          sent_at?: string;
          viewed_at?: string;
          responded_at?: string;
          response?: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          student_id: string;
          student_name: string;
          university: string;
          program: string;
          degree: string;
          total_amount: number;
          application_fee?: number;
          service_fee?: number;
          currency?: string;
          valid_until: string;
          include_visa?: boolean;
          include_accommodation?: boolean;
          include_insurance?: boolean;
          payment_plan?: string;
          status?: string;
          notes?: string;
          created_by?: string;
        };
        Update: {
          student_name?: string;
          university?: string;
          program?: string;
          degree?: string;
          total_amount?: number;
          application_fee?: number;
          service_fee?: number;
          currency?: string;
          valid_until?: string;
          include_visa?: boolean;
          include_accommodation?: boolean;
          include_insurance?: boolean;
          payment_plan?: string;
          status?: string;
          notes?: string;
          sent_at?: string;
          viewed_at?: string;
          responded_at?: string;
          response?: string;
          updated_at?: string;
        };
      };
      visa_appointments: {
        Row: {
          id: string;
          status: string;
          visa_type: string | null;
          communication_status: string;
          preferred_country: string | null;
          full_name: string;
          appointment_date: string | null;
          birth_date: string | null;
          passport_number: string | null;
          phone: string | null;
          email: string | null;
          application_center: string | null;
          secondary_country: string | null;
          pnr: string | null;
          service_type: string | null;
          visit_purpose: string | null;
          description: string | null;
          created_by: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          status?: string;
          visa_type?: string | null;
          communication_status?: string;
          preferred_country?: string | null;
          full_name: string;
          appointment_date?: string | null;
          birth_date?: string | null;
          passport_number?: string | null;
          phone?: string | null;
          email?: string | null;
          application_center?: string | null;
          secondary_country?: string | null;
          pnr?: string | null;
          service_type?: string | null;
          visit_purpose?: string | null;
          description?: string | null;
          created_by?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          status?: string;
          visa_type?: string | null;
          communication_status?: string;
          preferred_country?: string | null;
          full_name?: string;
          appointment_date?: string | null;
          birth_date?: string | null;
          passport_number?: string | null;
          phone?: string | null;
          email?: string | null;
          application_center?: string | null;
          secondary_country?: string | null;
          pnr?: string | null;
          service_type?: string | null;
          visit_purpose?: string | null;
          description?: string | null;
          updated_at?: string;
        };
      };
      branches: {
        Row: {
          id: string;
          name: string;
          city: string;
          address?: string;
          phone?: string;
          email?: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          city: string;
          address?: string;
          phone?: string;
          email?: string;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          name?: string;
          city?: string;
          address?: string;
          phone?: string;
          email?: string;
          updated_at?: string;
        };
      };
      universities: {
        Row: {
          id: string;
          name: string;
          country: string;
          city: string | null;
          ranking: number | null;
          website: string | null;
          description: string | null;
          requirements: string | null;
          tuition_fee_min: number | null;
          tuition_fee_max: number | null;
          currency: string;
          application_deadline: string | null;
          created_by: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          country?: string;
          city?: string | null;
          ranking?: number | null;
          website?: string | null;
          description?: string | null;
          requirements?: string | null;
          tuition_fee_min?: number | null;
          tuition_fee_max?: number | null;
          currency?: string;
          application_deadline?: string | null;
          created_by?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          name?: string;
          country?: string;
          city?: string | null;
          ranking?: number | null;
          website?: string | null;
          description?: string | null;
          requirements?: string | null;
          tuition_fee_min?: number | null;
          tuition_fee_max?: number | null;
          currency?: string;
          application_deadline?: string | null;
          created_by?: string | null;
          updated_at?: string;
        };
      };
      tasks: {
        Row: {
          id: string;
          title: string;
          description: string | null;
          due_date: string;
          completed: boolean;
          priority: string;
          student_id: string | null;
          created_by: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          title: string;
          description?: string | null;
          due_date: string;
          completed?: boolean;
          priority?: string;
          student_id?: string | null;
          created_by?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          title?: string;
          description?: string | null;
          due_date?: string;
          completed?: boolean;
          priority?: string;
          student_id?: string | null;
          updated_at?: string;
        };
      };
      deadlines: {
        Row: {
          id: string;
          title: string;
          description: string | null;
          due_date: string;
          completed: boolean;
          priority: string;
          type: string;
          student_id: string | null;
          created_by: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          title: string;
          description?: string | null;
          due_date: string;
          completed?: boolean;
          priority?: string;
          type?: string;
          student_id?: string | null;
          created_by?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          title?: string;
          description?: string | null;
          due_date?: string;
          completed?: boolean;
          priority?: string;
          type?: string;
          student_id?: string | null;
          updated_at?: string;
        };
      };
    };
  };
};